<template>
  <div v-if="showBreadcrumbs" class="breadcrumbs-wrapper">
    <HgBreadcrumb :links="breadcrumbs" :theme="platform" />
  </div>
</template>

<script setup lang="ts">
import { HgBreadcrumb, HgSupportedLanguage, SupportedPlatforms } from '@smg-real-estate/domus';
import type { GeoLocation } from '@smg-real-estate/geo-libs';
import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { isPlatformIs24 as checkIsPlatformIs24 } from '@/helpers/Platform/platform';
import { getBasePathWithLocation } from '@/router';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { useGeoStore } from '@/stores/geo';

interface BreadcrumbLink {
  name: string;
  link?: string;
}

const { t, locale } = useI18n();
const agencyProfilesStore = useAgencyProfilesStore();
const geoStore = useGeoStore();
const { searchedLocations } = storeToRefs(agencyProfilesStore);

const isPlatformIs24 = computed(() => checkIsPlatformIs24());

const platform = computed<SupportedPlatforms>(() =>
  isPlatformIs24.value ? SupportedPlatforms.is24 : SupportedPlatforms.hg,
);

const showBreadcrumbs = computed(() => {
  return searchedLocations.value && searchedLocations.value.length > 0;
});

/**
 * Get parent canton for the given location. Per SEO breadcrumb spec we only
 * surface the canton between "All Agencies" and the leaf location, regardless
 * of any intermediate region/district levels present in `parentPaths`.
 */
const getParentLocations = (location: GeoLocation): GeoLocation[] => {
  if (location.type === 'canton') {
    return [];
  }

  if (!location.parentPaths || location.parentPaths.length === 0) {
    return [];
  }

  const cantonId = location.parentPaths[0].find((id: string) => id.startsWith('geo-canton'));

  if (!cantonId) {
    return [];
  }

  return geoStore.getGeoLocationsById([cantonId]);
};

/**
 * Sort locations by hierarchy level (country → canton → place)
 */
const sortLocationsByHierarchy = (locations: GeoLocation[]): GeoLocation[] => {
  const hierarchyOrder = [
    'country',
    'canton',
    'district',
    'region',
    'place',
    'cityregion',
    'citydistrict',
    'zip',
  ];

  return [...locations].sort((a, b) => {
    const aIndex = hierarchyOrder.indexOf(a.type);
    const bIndex = hierarchyOrder.indexOf(b.type);
    return aIndex - bIndex;
  });
};

/**
 * Generate breadcrumb links from locations
 */
const generateLocationLinks = (locations: GeoLocation[]): BreadcrumbLink[] => {
  const sortedLocations = sortLocationsByHierarchy(locations);
  const language = locale.value as HgSupportedLanguage;
  return sortedLocations.map((location, index) => {
    const locationName = geoStore.getGeoLocationName(location.newId ?? location.id, language) ?? '';
    const urlName = location.urlNames[language] || location.urlNames[HgSupportedLanguage.DE];
    const isLast = index === sortedLocations.length - 1;

    return {
      name: locationName,
      link: isLast
        ? undefined
        : `${import.meta.env.VUE_APP_BASE_URL}${getBasePathWithLocation(language, urlName)}`,
    };
  });
};

const breadcrumbs = computed<BreadcrumbLink[]>(() => {
  if (!searchedLocations.value || searchedLocations.value.length === 0) {
    return [];
  }

  const language = locale.value as HgSupportedLanguage;

  const allAgenciesLink: BreadcrumbLink = {
    name: t('breadcrumbs.agencyDirectory'),
    link: `${import.meta.env.VUE_APP_BASE_URL}${getBasePathWithLocation(language)}`,
  };

  const primaryLocation = searchedLocations.value[0];
  const parentLocations = getParentLocations(primaryLocation);
  const allLocations = [...parentLocations, primaryLocation];
  const locationLinks = generateLocationLinks(allLocations);

  return [allAgenciesLink, ...locationLinks];
});

/**
 * Generate JSON-LD structured data for breadcrumbs (SEO)
 */
const generateBreadcrumbStructuredData = () => {
  if (!showBreadcrumbs.value || breadcrumbs.value.length === 0) {
    return [];
  }

  const itemListElement = breadcrumbs.value.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    ...(crumb.link ? { item: crumb.link } : {}),
  }));

  return [
    {
      type: 'application/ld+json' as const,
      textContent: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement,
      }),
    },
  ];
};

// Add structured data to page head for SEO
const breadcrumbStructuredData = computed(() => generateBreadcrumbStructuredData());

useHead({
  script: breadcrumbStructuredData,
});
</script>

<style lang="scss" scoped>
.breadcrumbs-wrapper > :deep([class*='HgBreadcrumb_breadcrumb'] ol) {
  li:last-child {
    pointer-events: none;
  }
}
</style>
